import { isValidFigmaUrl, type CodegenSettings, type GetFigmaFileResult } from '@animaapp/anima-sdk';
import { useAnimaCodegen, type UseAnimaParams } from '@animaapp/anima-sdk-react';
import { useLocation } from '@remix-run/react';
import { htmlEscape } from 'escape-goat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { removePublicDir } from './analyzer';
import { uploadBinaryFile, type Blob } from '~/lib/.client/api/binary-files';
import { createEmptyProject } from '~/lib/.client/api/projects';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { BOLT_BOOTSTRAP_ID } from '~/lib/constants';
import { connectToFigma, figmaProjectName, figmaURLFromRoute, isFigmaRedirect, resetFigmaRedirect } from '~/lib/figma';
import { importStore } from '~/lib/projects';
import type { ProjectData } from '~/lib/projects/entities';
import { hydrate } from '~/lib/projects/hydrate';
import { importPromise } from '~/lib/projects/importStore';
import { sendSentryError } from '~/lib/sentry.client';
import { figmaImportStore } from '~/lib/stores/figma';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { Message } from '~/types/chat';
import type { AppFiles } from '~/types/files';
import { fromBase64 } from '~/utils/base64';
import { logger } from '~/utils/logger';
import { navigateToStackblitzProject } from '~/utils/navigate';
import { withResolvers } from '~/utils/promises';
import { stripIndents } from '~/utils/stripIndent';

export enum StatusKind {
  Init,
  Importing,
  FigmaAuth,
  Done,
  Error,
}

type Status =
  | { type: StatusKind.FigmaAuth | StatusKind.Init }
  | { type: StatusKind.Error; error: Error }
  | {
      type: StatusKind.Done;
      messages: Message[];
      project: ProjectData;
    }
  | {
      type: StatusKind.Importing | StatusKind.Init;
      messages: Message[];
    };

export enum ErrorKind {
  Auth,
  NoResult,
  InvalidNode,
  MissingNode,
  CodegenBackend,
  RateLimit,
  AssetUploadSize,
  AssetUpload,
  FigmaFile,
}

type ExternalStatus =
  | {
      type: StatusKind.Error;
      error: Error;
    }
  | { type: Exclude<StatusKind, StatusKind.Error> };

export type { ExternalStatus as Status };

const defaultCodegenSettings: CodegenSettings = {
  framework: 'react',
  language: 'typescript',
  styling: 'tailwind',
  uiLibrary: 'shadcn',

  // helps getting less flattened layers as images
  disableMarkedForExport: true,
};

export type Tasks = ReturnType<typeof useAnimaCodegen>['tasks'];

export function useFigmaImport(connected: boolean) {
  const [status, setStatus] = useState<ExternalStatus>({ type: StatusKind.Init });

  const onStatus = useCallback((status: Status) => {
    switch (status.type) {
      case StatusKind.Importing: {
        importStore.setKey('messages', status.messages);

        break;
      }
      case StatusKind.FigmaAuth: {
        importStore.setKey('statusMessage', 'Connecting to Figma');

        break;
      }
      case StatusKind.Done: {
        hydrate(status.project);
        navigateToStackblitzProject(status.project.slug, { clearSearch: true });
        importStore.setKey('messages', status.messages);
        projectStore.updateProject(status.project, userStore.getActiveOrganization().get());
        importPromise.resolve();

        break;
      }
      case StatusKind.Error: {
        logger.error(status.error);

        importStore.setKey('statusMessage', status.error.message);
        importPromise.reject(status.error);

        break;
      }
      default: {
        importStore.setKey('statusMessage', '');

        break;
      }
    }

    setStatus(status);
  }, []);

  const { messages, tasks, target } = useFigmaImportFlow({
    connected,
    onStatus,
  });

  const fetchStatus = tasks.fetchDesign.status;
  const codeGenStatus = tasks.codeGeneration.status;

  useEffect(() => {
    if (fetchStatus === 'finished') {
      figmaImportStore.fetch.resolve();
    }

    if (codeGenStatus === 'finished') {
      figmaImportStore.generate.resolve();
    }
  }, [fetchStatus, codeGenStatus]);

  useEffect(() => {
    importPromise.promise
      .then(
        () => ({ success: true, error: undefined }),
        (error) => ({ success: false, error }),
      )
      .then(({ success, error }) => {
        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: success ? AnalyticsTrackEvent.ImportedFigmaFrame : AnalyticsTrackEvent.FailedFigmaFrameImport,
            properties: {
              error: success ? undefined : errorToEvent(error),
            },
          },
        });

        if (!success) {
          sendSentryError(error);
        }
      });
  }, []);

  const effectiveTasks = useMemo(() => {
    return {
      ...tasks,
      uploadAssets: {
        status: tasks.codeGeneration.status === 'finished' ? 'running' : 'pending',
      },
    } satisfies Tasks;
  }, [tasks]);

  return {
    messages,
    status,
    target,
    tasks: effectiveTasks,
  };
}

function useFigmaImportFlow({
  connected,
  onStatus: setStatus,
}: {
  connected?: boolean;
  onStatus: (status: Status) => void;
}) {
  const currentLocation = useLocation();

  const [redirected] = useState(() => isFigmaRedirect());

  const [{ figmaURL, target }] = useState(() => {
    const figmaURL = figmaURLFromRoute(currentLocation);

    const result = isValidFigmaUrl(figmaURL.toString());
    const fileKey = result[1];

    const target = figmaProjectName(figmaURL.toString()) ?? fileKey;

    return { figmaURL: figmaURL.toString(), target };
  });

  const { getCode: hookGetCode, tasks } = useAnimaCodegen({ url: '/api/anima' });

  const getCodeRef = useRef(hookGetCode);

  useEffect(() => {
    getCodeRef.current = hookGetCode;
  });

  const [messages] = useState<Message[]>(() => [getInitialMessage(target)]);

  async function doEffect() {
    resetFigmaRedirect();

    const [isValid, fileKey, nodeId] = isValidFigmaUrl(figmaURL);

    setStatus({ type: StatusKind.Importing, messages: [getInitialMessage(target)] });

    // FIXME: can we handle empty nodeId?
    if (!isValid || !nodeId) {
      throw new FigmaError(ErrorKind.MissingNode);
    }

    if (!redirected) {
      if (!connected) {
        setStatus({ type: StatusKind.FigmaAuth });

        const success = await connectToFigma();

        if (!success) {
          throw new FigmaError(ErrorKind.Auth);
        }
      }
    }

    const figmaFileURL = new URL(`/api/figma/file/${fileKey}`, window.location.origin);
    figmaFileURL.searchParams.set('ids', nodeId);

    // start codegen
    const codegenPromise = getCodeRef.current({
      fileKey,
      nodesId: [nodeId],
      settings: defaultCodegenSettings,
      assetsStorage: {
        strategy: 'local' as const,
        filePath: 'public',
        referencePath: '',
      },
    } satisfies UseAnimaParams);

    // await this fetch as its errors are more informative than Anima's
    const figmaFile = await clientFetch(figmaFileURL)
      .then((response) => {
        if (response.ok) {
          return response.json<GetFigmaFileResult>();
        } else {
          throw response;
        }
      })
      .catch(async (error) => {
        if (error instanceof Response) {
          throw await FigmaError.fromFile(error);
        }

        throw error;
      });

    const { result, error } = await codegenPromise;

    if (error) {
      throw FigmaError.fromCodegen(error);
    }

    if (!result) {
      throw new FigmaError(ErrorKind.NoResult);
    }

    const binaryFiles = Object.entries(result.files).flatMap(([path, file]) =>
      file.isBinary ? [{ path, contents: file.content }] : [],
    );

    const appFiles: AppFiles = Object.fromEntries(
      Object.entries(result.files).flatMap(([path, file]) => {
        if (file.isBinary) {
          return [];
        }

        return [
          [
            path,
            {
              contents: file.content,
              isBinary: file.isBinary,
              type: 'file' as const,
              fullPath: path,
              lastModified: Date.now(),
              name: path.split('/').at(-1)!,
            },
          ],
        ];
      }),
    );

    addBoltIgnore(appFiles);
    patchViteConfig(appFiles);

    const projectData = { title: figmaFile?.name ?? 'Import from Figma project' };

    const project = await createEmptyProject(userStore.getActiveOrganization().get(), {
      project: {
        title: projectData.title,
        appFiles,
      },
    });

    const blobs = await uploadFiles(project.slug, binaryFiles).catch((error) => {
      throw FigmaError.fromUpload(error);
    });

    for (const [i, binaryFile] of binaryFiles.entries()) {
      const blob = blobs[i];

      // FIXME: we need to ensure that this is properly updated on SB
      appFiles[binaryFile.path] = {
        type: 'file',
        isBinary: true,
        blobId: blob.signedId,
        lastModified: Date.now(),
        name: blob.name,
        fullPath: binaryFile.path,
        contents: blob.content,
      };
    }

    setStatus({
      type: StatusKind.Done,
      messages: [getInitialMessage(target)],
      project: { ...project, ...projectData, description: projectData.title, appFiles, integrations: null },
    });
  }

  useEffect(() => {
    doEffect().catch((error) => {
      setStatus({ type: StatusKind.Error, error });
    });
  }, [figmaURL, target, redirected]);

  return { messages, tasks, target };
}

async function uploadFiles(slug: string, files: Array<{ path: string; contents: string }>) {
  const ongoingUploads: Array<Promise<Blob>> = [];

  const globalFailure = withResolvers<never>();

  for (const file of files) {
    const filename = file.path.split('/').pop()!;

    const fileObject = new File([fromBase64(file.contents)], filename) as File & { path: string };

    // we only await sequentially API requests to StackBlitz
    const uploadStarted = withResolvers<void>();

    const uploadPromise = uploadBinaryFile(slug, filename, fileObject, uploadStarted);

    uploadPromise.catch(globalFailure.reject);

    await Promise.race([uploadStarted.promise, globalFailure.promise]);

    ongoingUploads.push(uploadPromise);
  }

  // we await all S3 uploads in parallel
  return await Promise.all(ongoingUploads);
}

function getInitialMessage(target: string): Message {
  const content = stripIndents`
    I'm importing your Figma frame into Bolt. This may take a moment as I set everything up. Once it's ready, you'll be able to interact with an implementation of your design.

    <boltArtifact id="figma-import" title="Import ${htmlEscape(target)} from Figma">
      <boltAction type="import" kind="figma" target="${htmlEscape(target)}"></boltAction>
    </boltArtifact>

    I've imported your Figma frame. You can start prompting now to add more features or improve the code. For instance, you could try making it responsive or adding more interactivity
  `;

  return {
    id: BOLT_BOOTSTRAP_ID,
    type: 'chat',
    role: 'assistant',
    status: 'complete',
    content,
    ignore: true,
    merge: false,
    parts: [],
  };
}

type SimpleFigmaError = Exclude<ErrorKind, ErrorKind.FigmaFile>;
type FigmaErrorPayload = { kind: SimpleFigmaError } | { kind: ErrorKind.FigmaFile; status: number };

export class FigmaError extends Error {
  payload: FigmaErrorPayload;
  name = 'FigmaError';

  constructor(payloadOrKind: FigmaErrorPayload | SimpleFigmaError, cause?: any) {
    super('', { cause });

    this.payload = typeof payloadOrKind === 'object' ? payloadOrKind : { kind: payloadOrKind };
  }

  static fromCodegen(cause: Error) {
    const unsupported =
      /Selected node type is not supported/i.test(cause.message) ||
      /Selected node is a page with multiple children/i.test(cause.message);

    if (unsupported) {
      return new FigmaError(ErrorKind.InvalidNode, cause);
    }

    if (cause.message.includes('Non-200 status code (429)')) {
      return new FigmaError(ErrorKind.RateLimit, cause);
    }

    return new FigmaError(ErrorKind.CodegenBackend, cause);
  }

  static fromUpload(cause: any) {
    if (cause instanceof XMLHttpRequest && cause.response?.message) {
      // FIXME: needs a code from Rails
      const size = cause.response.message.startsWith('Individual files uploaded to StackBlitz cannot exceed');

      if (size) {
        return new FigmaError(ErrorKind.AssetUploadSize, cause);
      }

      return new FigmaError(ErrorKind.AssetUpload, cause.response);
    }

    return new FigmaError(ErrorKind.AssetUpload, cause);
  }

  static async fromFile(error: Response) {
    return new FigmaError(
      { kind: ErrorKind.FigmaFile, status: error.status },
      {
        cause: { text: await error.text().catch(() => '') },
      },
    );
  }
}

function addBoltIgnore(appFiles: AppFiles) {
  const needsIgnore = Object.entries(appFiles).some(
    ([path, file]) => file.type === 'file' && path.startsWith('src/components/ui/'),
  );

  if (!needsIgnore || appFiles['.bolt/ignore']) {
    return;
  }

  appFiles['.bolt/ignore'] = {
    contents: 'src/components/ui/*\n',
    isBinary: false,
    type: 'file' as const,
    fullPath: '.bolt/ignore',
    lastModified: Date.now(),
    name: 'ignore',
  };
}

function errorToEvent(error: any) {
  if (!(error instanceof FigmaError)) {
    return 'unknown';
  }

  switch (error.payload.kind) {
    case ErrorKind.Auth: {
      return 'auth';
    }
    case ErrorKind.InvalidNode: {
      return 'invalid-node';
    }
    case ErrorKind.MissingNode: {
      return 'missing-node';
    }
    case ErrorKind.CodegenBackend: {
      return 'codegen-backend';
    }
    case ErrorKind.AssetUploadSize: {
      return 'asset-size';
    }
    case ErrorKind.AssetUpload: {
      return 'asset-upload';
    }
    case ErrorKind.RateLimit: {
      return 'rate-limit';
    }
    case ErrorKind.NoResult: {
      return 'no-result';
    }
    case ErrorKind.FigmaFile: {
      return 'figma-file';
    }
    default: {
      return 'unknown-figma';
    }
  }
}

function patchViteConfig(appFiles: AppFiles) {
  const viteConfig = appFiles['vite.config.ts'];

  if (viteConfig?.type === 'file') {
    viteConfig.contents = removePublicDir(viteConfig.contents);
  }
}
